import { RootState } from '@Common/models/redux/store';
import { ArrowBack } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { Fade, Grid, IconButton, Portal, Theme, useMediaQuery } from '@quillbot/design-system';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  useCitationGeneratorDispatch,
  useCitationGeneratorState,
} from 'src/citation-generator/context';
import {
  stopSearchMode,
  toggleCitationGeneratorEditModal,
} from 'src/citation-generator/context/actionCreators';
import { getCgBreakpointDown } from 'src/citation-generator/utils';
import { LAYOUT_VALS } from 'src/researcher/constants/common.constants';
import { tab_height } from 'src/researcher/styles/constants';
import { unitPX, UNITS } from 'src/researcher/styles';
import { CitationGeneratorXPath } from 'src/citation-generator/constants/xpath.constants';
import {
  ContributorField,
  DateFieid,
  Dropdown,
  Pages,
  SaveButton,
  Section,
  SourceTypeDropDown,
  TextField,
  TextFieldGroup,
  WarningBanner,
} from './components';
import { useEditFormDispatch, useEditFormState } from './context';
import { resetState, setAccessDate, setPublishDate, setTextValueOf } from './actionCreators';

const useCitationEditFormStyle = makeStyles((theme) => ({
  marginBotton: {
    marginBottom: 10,
  },
  addAuthorBtn: {
    borderColor: theme.palette.divider,
  },
  inputSection: {
    paddingLeft: '75px',
  },
  bottomBar: {
    height: '48px',
    boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.05)',
    position: 'sticky',
    bottom: 0,
    marginTop: '2px',
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    // backgroundColor: 'red',
    [getCgBreakpointDown(theme)]: {
      // width: '100vw',
      boxSizing: 'border-box',
      marginLeft: -13,
      boxShadow: 'none',
    },
  },
  mainContainer: {
    position: 'absolute',
    top: ({ pathname }: { pathname: string }) =>
      pathname.includes('/co-writer')
        ? `calc(${LAYOUT_VALS.bannerHeight} + ${LAYOUT_VALS.maintenanceHeight} + ${LAYOUT_VALS.drawerMenubarHeight})`
        : 0,
    right: 0,
    left: 0,
    bottom: 0,
    zIndex: ({ pathname }: { pathname: string }) => (pathname.includes('/co-writer') ? 2 : 1),
    paddingRight: 4,
    backgroundColor: theme.palette.background.paper,
    [getCgBreakpointDown(theme)]: {
      // border: '1px solid green',
      minHeight: 'calc(100vh - 50px)',
      maxHeight: 'calc(100vh - 50px)',
      // minHeight: 'calc(100vh - 49px)',
      // marginTop: 12,
      position: 'relative',
    },
  },
  containerbackgroundColor: {
    backgroundColor: theme.palette.background.paper,
    display: 'block',
    position: 'relative',
    [getCgBreakpointDown(theme)]: {
      paddingLeft: 12,
      marginBottom: 40,
    },
  },
  backBtnAndInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [getCgBreakpointDown(theme)]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  backBtn: {
    padding: '5px',
    marginLeft: '15px',
    marginTop: '5px',
    '& > .btnText': {
      display: 'none',
      fontSize: 12,
    },
    [getCgBreakpointDown(theme)]: {
      borderRadius: 50,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      paddingRight: 22,
      marginLeft: 0,
      marginBottom: 12,
      '& > .btnText': {
        display: 'initial',
      },
      '& > .btnArrow': {
        marginRight: 8,
        fontSize: 14,
      },
    },
  },
  formWrapper: {
    paddingRight: '45px',
    paddingBottom: ({ pathname }: { pathname: string }) =>
      pathname.includes('/co-writer') ? 50 : undefined,
    [getCgBreakpointDown(theme)]: {
      paddingRight: 12,
      paddingBottom: 0,
    },
  },
  saveBtnContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const useFormEffects = (topElRef: React.RefObject<HTMLDivElement>) => {
  const open = useCitationGeneratorState((state) => state.isEditFormOpen);
  const isSmallScreen = useMediaQuery<Theme>((theme) => getCgBreakpointDown(theme));
  const { sourceType, contributors } = useEditFormState((state) => state);

  const alignEducationArea = (reset?: boolean) => {
    const editForm = document.getElementById('citation-edit-form-container');
    const educationArea = document.getElementById('education-area-wrapper');

    if (educationArea && editForm) {
      educationArea.style.marginTop = '0px';
      if (reset) {
        return;
      }

      if (educationArea.offsetTop > editForm.offsetHeight) {
        educationArea.style.marginTop = `${editForm.offsetHeight}px`;
      } else {
        educationArea.style.marginTop = `${editForm.offsetHeight - educationArea.offsetTop}px`;
      }
    }
  };

  useEffect(() => {
    if (isSmallScreen) {
      setTimeout(alignEducationArea, 10, !open);
    }
  }, [open, sourceType, contributors]);

  useEffect(() => {
    if (open) {
      topElRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });

      if (isSmallScreen) {
        window.scrollTo(0, 0);
      }
    }
  }, [open]);
};

interface Props {
  paperRef: React.RefObject<HTMLElement>;
  cgrContainerRef?: React.RefObject<HTMLElement>;
  midContainerRef?: React.RefObject<HTMLElement>;
}

export default function EditForm({ paperRef, cgrContainerRef, midContainerRef }: Props) {
  const editFormDispatch = useEditFormDispatch();
  const openInFullWidth = useMediaQuery('(min-width:1200px)');
  const router = useRouter();
  const open = useCitationGeneratorState((state) => state.isEditFormOpen);
  const independentTool = useSelector((state: RootState) => state.common.independentTool);
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  const isSmallScreen = useMediaQuery<Theme>((theme) => getCgBreakpointDown(theme));
  const topElRef = useRef<HTMLDivElement>(null);
  useFormEffects(topElRef);

  const getContainer = () => {
    if (isSmallScreen && midContainerRef) return midContainerRef.current;

    if (openInFullWidth && cgrContainerRef) return cgrContainerRef.current;

    return paperRef.current;
  };

  const classes = useCitationEditFormStyle(router);

  const getHeight = () => {
    if (isSmallScreen) return undefined;

    if (independentTool) return '86vh';

    if (router.pathname.includes('/co-writer')) {
      return `calc(${tab_height} - ${LAYOUT_VALS.drawerMenubarHeight} - ${unitPX(
        UNITS['5pt']
      )} - ${unitPX(UNITS['7pt'])})`;
    }

    return 'calc(50vh + 28px)';
  };

  return (
    <Portal container={getContainer()}>
      <Fade style={{ display: open ? 'block' : 'none' }} in={open}>
        <Grid container className={classes.mainContainer}>
          <Grid
            container
            className={classes.containerbackgroundColor}
            id="citation-edit-form-container"
          >
            <Grid item container className={classes.backBtnAndInfo}>
              <IconButton
                onClick={() => {
                  citationGeneratorDispatch(toggleCitationGeneratorEditModal(false));
                  citationGeneratorDispatch(stopSearchMode());
                  editFormDispatch(resetState());
                  // citationGeneratorTrack.userClickedBackButton();
                }}
                size="large"
                className={classes.backBtn}
              >
                <ArrowBack className="btnArrow" />

                <span className="btnText">Back to citation list</span>
              </IconButton>

              <WarningBanner />

              <Grid />
            </Grid>

            <Grid
              container
              style={{
                maxHeight: getHeight(),
                height: getHeight(),
                overflow: 'auto',
                marginBottom: 10,
                display: 'block',
              }}
            >
              <Grid
                container
                className={classes.formWrapper}
                style={{
                  marginBottom: router.pathname.includes('/co-writer')
                    ? unitPX(UNITS['12pt'])
                    : undefined,
                  paddingLeft: router.pathname.includes('/co-writer')
                    ? unitPX(UNITS['4pt'])
                    : undefined,
                }}
              >
                <Grid container ref={topElRef}>
                  <SourceTypeDropDown />

                  <TextField
                    valueKey="title"
                    setter={setTextValueOf}
                    inputProps={{ ...CitationGeneratorXPath.EditFormTitleInput }}
                  />
                </Grid>

                <Section title="Contributors">
                  <ContributorField />
                </Section>

                <Section title="Publication Information">
                  <TextField
                    valueKey="containerTitle"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <TextField
                    valueKey="url"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <Dropdown
                    valueKey="status"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <TextField
                    valueKey="publisher"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <TextFieldGroup
                    valueKeys={['city', 'state']}
                    setter={setTextValueOf}
                    label="Place"
                    rootClass={classes.marginBotton}
                  />

                  <DateFieid
                    valueKey="publishDate"
                    setter={setPublishDate}
                    rootClass={classes.marginBotton}
                  />

                  <DateFieid
                    valueKey="accessDate"
                    setter={setAccessDate}
                    rootClass={classes.marginBotton}
                  />

                  <TextField
                    valueKey="doi"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <TextField valueKey="source" setter={setTextValueOf} />
                </Section>

                <Section title="Advanced Information" isAdvanced>
                  <Pages setter={setTextValueOf} rootClass={classes.marginBotton} />

                  <TextField
                    valueKey="volume"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <TextField
                    valueKey="totalVolumes"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <TextField
                    valueKey="issue"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <TextField
                    valueKey="edition"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <TextField
                    valueKey="collectionTitle"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />

                  <TextField
                    valueKey="medium"
                    setter={setTextValueOf}
                    rootClass={classes.marginBotton}
                  />
                </Section>
              </Grid>
            </Grid>

            <Grid
              container
              className={classes.bottomBar}
              justifyContent="center"
              alignItems="center"
            >
              <SaveButton topElRef={topElRef} />
            </Grid>
          </Grid>
        </Grid>
      </Fade>
    </Portal>
  );
}
